import { faPen, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import HtmlContent from '@sharedComponents/HtmlContent';
import InformationMessage from '@sharedComponents/InformationMessage';
import Loader from '@sharedComponents/Loader';
import Panel from '@sharedComponents/Panel';
import SlateEditor from '@sharedComponents/SlateEditor';
import { decodeHtmlEntities } from '@sharedComponents/SlateEditor/utils';
import Typography from '@sharedComponents/Typography';
import { formatDate, formatTime } from '@utils/date';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as style from './style.module.css';

const authorName = (user) => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

/**
 * "Context" tab of a client or platform page: shared HTML notes, shown read-only and edited with the
 * SlateEditor. `onSave(html)` resolves to the saved context, or to null when the save failed (the
 * editor then stays open). `onDirtyChange(bool)` reports whether the editor holds unsaved changes.
 * `maxLength` is the maximum text length in the editor, null for no limit.
 */
const ContextTab = ({
    context = null,
    loading = false,
    error = null,
    readOnly = false,
    emptyTitle = '',
    emptyDescription = '',
    onSave = async () => null,
    onDirtyChange = () => {},
    maxLength = null,
}) => {
    const { t } = useTranslation('bo');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [length, setLength] = useState(0);
    const [saving, setSaving] = useState(false);

    if (loading) return <Loader />;
    if (error) return <InformationMessage status="error" text={t('The context could not be loaded.')} />;

    const html = context?.html ?? null;

    const startEditing = () => {
        setDraft(null);
        setLength(html ? decodeHtmlEntities(html).length : 0);
        setEditing(true);
    };

    const stopEditing = () => {
        setEditing(false);
        setDraft(null);
        onDirtyChange(false);
    };

    const handleChange = ({ serialize, raw }) => {
        if (draft === null) onDirtyChange(true);
        setDraft(serialize);
        setLength(raw.length);
    };

    const handleSave = async () => {
        setSaving(true);
        let saved = null;
        try {
            saved = await onSave(draft);
        } catch {
            // A rejected save keeps the editor open, like a failed one: reporting it is the caller's job.
        }
        setSaving(false);
        if (saved) stopEditing();
    };

    const lastModified = () => {
        if (!context?.updatedAt) return null;
        const values = { date: formatDate(context.updatedAt), time: formatTime(context.updatedAt) };
        return context.updatedBy
            ? t('Last modified on {{date}} at {{time}} by {{author}}', { ...values, author: authorName(context.updatedBy) })
            : t('Last modified on {{date}} at {{time}}', values);
    };

    if (editing) {
        return (
            <FlexBox flexDirection="column" gap="s">
                <SlateEditor
                    initialValue={html ?? ''}
                    withLinks
                    placeholder={t('Write the context here')}
                    maxLength={maxLength}
                    onChangeRaw={handleChange}
                />
                <FlexBox justifyContent="space-between" gap="s">
                    <Typography fontSize="s" color="neutral-secondary">
                        {t('Shortcuts: Ctrl+B bold, Ctrl+I italic, Ctrl+U underline')}
                    </Typography>
                    <Typography fontSize="s" color="neutral-secondary">
                        {maxLength === null
                            ? t('Number of characters: {{length}}', { length })
                            : t('Number of characters: {{length}}/{{max}}', { length, max: maxLength.toLocaleString() })}
                    </Typography>
                </FlexBox>
                <FlexBox justifyContent="flex-end" gap="s">
                    <Button outline label={t('Cancel')} action={stopEditing} disabled={saving} />
                    <Button label={t('Save')} action={handleSave} disabled={draft === null || saving} isLoading={saving} />
                </FlexBox>
            </FlexBox>
        );
    }

    if (!html) {
        return (
            <FlexBox flexDirection="column" alignItems="center" gap="s" className={style.empty}>
                <Typography fontWeight="bold">{emptyTitle}</Typography>
                {!readOnly && (
                    <Typography color="neutral-secondary" center>
                        {emptyDescription}
                    </Typography>
                )}
                {!readOnly && <Button icon={faPlus} label={t('Add a context')} action={startEditing} />}
            </FlexBox>
        );
    }

    return (
        <FlexBox flexDirection="column" gap="s">
            <FlexBox justifyContent="space-between" alignItems="center" gap="s">
                <Typography fontSize="s" color="neutral-secondary">
                    {lastModified()}
                </Typography>
                {!readOnly && <Button outline icon={faPen} label={t('Edit the context')} action={startEditing} />}
            </FlexBox>
            <Panel className={style.card}>
                <HtmlContent html={html} />
            </Panel>
        </FlexBox>
    );
};

export default ContextTab;
